<script setup>
import { ref, computed, onBeforeUnmount, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useEmitter } from 'dashboard/composables/emitter';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  isAConversationRoute,
  isAInboxViewRoute,
  isNotificationRoute,
} from 'dashboard/helper/routeHelpers';
import { useEventListener } from '@vueuse/core';
import { Refresh01Icon } from '@hugeicons/core-free-icons';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import HugeIcon from 'dashboard/components-next/icon/HugeIcon.vue';

const emit = defineEmits(['reconnecting']);
const { t } = useI18n();
const route = useRoute();

const RECONNECTED_BANNER_TIMEOUT = 2000;

const showNotification = ref(!navigator.onLine);
const isDisconnected = ref(false);
const isReconnecting = ref(false);
const isReconnected = ref(false);
let reconnectTimeout = null;
watch(isReconnecting, value => emit('reconnecting', value), {
  immediate: true,
});

const mobileStatus = computed(() => {
  if (isReconnecting.value) return t('NETWORK.MOBILE.RECONNECTING');
  if (isReconnected.value) return t('NETWORK.MOBILE.CONNECTED');
  return t('NETWORK.MOBILE.OFFLINE');
});

const bannerText = computed(() => {
  if (isReconnecting.value) return t('NETWORK.NOTIFICATION.RECONNECTING');
  if (isReconnected.value) return t('NETWORK.NOTIFICATION.RECONNECT_SUCCESS');
  return t('NETWORK.NOTIFICATION.OFFLINE');
});

const iconName = computed(() => {
  if (isReconnecting.value) return 'i-lucide-loader-circle';
  if (isReconnected.value) return 'i-lucide-wifi';
  return 'i-lucide-wifi-off';
});

const notificationStyle = computed(() => {
  if (isReconnected.value) {
    return {
      container:
        'border-n-green-6/50 bg-n-green-3 text-n-green-12 dark:border-n-green-7/40 dark:bg-n-green-4/30 dark:text-n-green-11',
      icon: 'bg-n-green-5/70 text-n-green-11 dark:bg-n-green-8/30',
    };
  }

  if (isReconnecting.value) {
    return {
      container:
        'border-n-blue-6/50 bg-n-blue-3 text-n-blue-12 dark:border-n-blue-7/40 dark:bg-n-blue-4/30 dark:text-n-blue-11',
      icon: 'bg-n-blue-5/70 text-n-blue-11 dark:bg-n-blue-8/30',
    };
  }

  return {
    container: 'border-transparent bg-n-amber-6 text-n-slate-12',
    icon: 'bg-[rgb(250,137,0)] text-white',
  };
});

const canRefresh = computed(
  () => !isReconnecting.value && !isReconnected.value
);

const refreshPage = () => {
  window.location.reload();
};

const closeNotification = () => {
  showNotification.value = false;
  isReconnected.value = false;
  clearTimeout(reconnectTimeout);
};

const isInAnyOfTheRoutes = routeName => {
  return (
    isAConversationRoute(routeName, true) ||
    isAInboxViewRoute(routeName, true) ||
    isNotificationRoute(routeName, true)
  );
};

const updateWebsocketStatus = () => {
  clearTimeout(reconnectTimeout);
  isReconnecting.value = false;
  isReconnected.value = false;
  isDisconnected.value = true;
  showNotification.value = true;
};

const handleReconnectionCompleted = () => {
  clearTimeout(reconnectTimeout);
  isDisconnected.value = false;
  isReconnecting.value = false;
  isReconnected.value = true;
  showNotification.value = true;
  reconnectTimeout = setTimeout(closeNotification, RECONNECTED_BANNER_TIMEOUT);
};

const handleReconnecting = () => {
  clearTimeout(reconnectTimeout);
  if (route.meta.mobile || isInAnyOfTheRoutes(route.name)) {
    isReconnecting.value = true;
    isReconnected.value = false;
    showNotification.value = true;
  } else {
    handleReconnectionCompleted();
  }
};

const updateOnlineStatus = event => {
  // Case: Websocket is not disconnected
  // If the app goes offline, show the notification
  // If the app goes online, close the notification

  // Case: Websocket is disconnected
  // If the app goes offline, show the notification
  // If the app goes online but the websocket is disconnected, don't close the notification
  // If the app goes online and the websocket is not disconnected, close the notification

  if (event.type === 'offline') {
    clearTimeout(reconnectTimeout);
    isReconnecting.value = false;
    isReconnected.value = false;
    showNotification.value = true;
  } else if (event.type === 'online' && !isDisconnected.value) {
    handleReconnectionCompleted();
  }
};

useEventListener('online', updateOnlineStatus);
useEventListener('offline', updateOnlineStatus);
useEmitter(BUS_EVENTS.WEBSOCKET_DISCONNECT, updateWebsocketStatus);
useEmitter(
  BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED,
  handleReconnectionCompleted
);
useEmitter(BUS_EVENTS.WEBSOCKET_RECONNECT, handleReconnecting);

onBeforeUnmount(() => {
  clearTimeout(reconnectTimeout);
});
</script>

<template>
  <Transition
    enter-active-class="transition duration-200 ease-out"
    enter-from-class="translate-y-2 scale-95 opacity-0"
    enter-to-class="translate-y-0 scale-100 opacity-100"
    leave-active-class="transition duration-150 ease-in"
    leave-from-class="translate-y-0 scale-100 opacity-100"
    leave-to-class="translate-y-2 scale-95 opacity-0"
  >
    <div
      v-if="showNotification"
      role="status"
      aria-live="polite"
      class="fixed bottom-5 left-1/2 z-50 w-max max-w-[calc(100vw-2rem)] -translate-x-1/2"
    >
      <div
        v-if="route.meta.mobile"
        class="flex items-center gap-2 rounded-full bg-n-solid-2 px-3 py-1.5 text-xs font-medium text-n-slate-12 shadow-lg"
      >
        <span
          aria-hidden="true"
          class="size-1.5 rounded-full"
          :class="
            isReconnected
              ? 'bg-n-green-9'
              : isReconnecting
                ? 'bg-n-brand'
                : 'bg-n-amber-9'
          "
        />
        {{ mobileStatus }}
      </div>
      <div
        v-else
        class="flex min-w-[180px] items-center gap-2.5 rounded-lg border px-2 py-1.5 shadow-lg shadow-black/10 backdrop-blur-xl dark:shadow-black/30"
        :class="notificationStyle.container"
      >
        <span
          class="flex size-8 shrink-0 items-center justify-center rounded-lg"
          :class="notificationStyle.icon"
        >
          <Icon
            :icon="iconName"
            :class="isReconnecting ? 'size-5 animate-spin' : 'size-4'"
          />
        </span>

        <span class="min-w-0 flex-1 text-sm font-medium leading-5">
          {{ bannerText }}
        </span>

        <div class="flex shrink-0 items-center gap-1">
          <button
            v-if="canRefresh"
            type="button"
            class="flex size-6 items-center justify-center rounded-md bg-transparent text-current transition-opacity hover:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-current"
            :title="$t('NETWORK.BUTTON.REFRESH')"
            :aria-label="$t('NETWORK.BUTTON.REFRESH')"
            @click="refreshPage"
          >
            <HugeIcon :icon="Refresh01Icon" :size="18" class="shrink-0" />
          </button>

          <button
            type="button"
            class="flex size-6 items-center justify-center rounded-md bg-transparent text-current transition-opacity hover:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-current"
            :title="$t('GENERAL.CLOSE')"
            :aria-label="$t('GENERAL.CLOSE')"
            @click="closeNotification"
          >
            <Icon icon="i-lucide-x" class="size-[18px] shrink-0" />
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>
